import { zodResolver } from '@hookform/resolvers/zod'
import { History, Loader2, Plus } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Textarea } from '@/components/ui/textarea'
import { isApiError } from '@/lib/api/errors'
import { fmtDate } from '../../../shared/format'
import { useModuleContent } from '../../catalogApi'
import { todayIso } from '../../taskFilters'
import type { CatalogModuleView, ModuleChangelogEntry } from '../../types'
import { toastError } from '../../ui'
import { RowMenu } from '../RowMenu'

export function ModuleChangelog({ module: m, canWrite }: { module: CatalogModuleView; canWrite: boolean }) {
  const { t } = useTranslation()
  const { deleteChangelog } = useModuleContent(m.id)
  const [dialog, setDialog] = useState<{ open: boolean; entry: ModuleChangelogEntry | null }>({ open: false, entry: null })

  const onDelete = async (c: ModuleChangelogEntry) => {
    if (!(await confirmModal({ title: t('projects.catalog.changelog.deleteTitle'), description: t('projects.catalog.changelog.deleteBody', { version: c.version }), confirmLabel: t('common.delete'), destructive: true }))) return
    deleteChangelog.mutate(c.id, { onSuccess: () => toast.success(t('common.deleted')), onError: toastError })
  }

  return (
    <section className="rounded-xl border bg-card shadow-card">
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <History className="size-4 text-muted-foreground" aria-hidden />
        <h2 className="flex-1 font-semibold">{t('projects.catalog.changelog.title')}</h2>
        {canWrite ? (
          <Button size="sm" variant="outline" onClick={() => setDialog({ open: true, entry: null })}>
            <Plus />
            {t('projects.catalog.changelog.add')}
          </Button>
        ) : null}
      </header>
      {m.changelog.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">{t('projects.catalog.changelog.empty')}</p>
      ) : (
        <ol className="space-y-4 p-4">
          {m.changelog.map((c, i) => (
            <li key={c.id} className="relative flex gap-3">
              <span className={i === 0 ? 'mt-1 size-3 shrink-0 rounded-full bg-primary ring-4 ring-primary/15' : 'mt-1 size-3 shrink-0 rounded-full border-2 border-border bg-card'} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
                  <span className="font-semibold">v{c.version}</span>
                  <span className="text-xs tabular-nums text-muted-foreground">{fmtDate(c.date)}</span>
                </p>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{c.notes}</p>
              </div>
              {canWrite ? <RowMenu label={`v${c.version}`} onEdit={() => setDialog({ open: true, entry: c })} onDelete={() => void onDelete(c)} /> : null}
            </li>
          ))}
        </ol>
      )}
      {dialog.open ? <ChangelogForm key={dialog.entry?.id ?? 'new'} module={m} entry={dialog.entry} onDone={() => setDialog({ open: false, entry: null })} /> : null}
    </section>
  )
}

function ChangelogForm({ module: m, entry, onDone }: { module: CatalogModuleView; entry: ModuleChangelogEntry | null; onDone: () => void }) {
  const { t } = useTranslation()
  const { saveChangelog } = useModuleContent(m.id)
  const schema = z.object({
    version: z.string().trim().regex(/^\d+(\.\d+){0,2}(-[0-9A-Za-z.]+)?$/, t('projects.catalog.changelog.versionInvalid')),
    date: z.string().min(1, t('projects.form.dateRequired')),
    notes: z.string().trim().min(3, t('projects.catalog.changelog.notesRequired')).max(500),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { version: entry?.version ?? '', date: entry?.date ?? todayIso(), notes: entry?.notes ?? '' } })
  const { errors } = form.formState

  const onSubmit = form.handleSubmit(async (input) => {
    try {
      await saveChangelog.mutateAsync({ id: entry?.id, input })
      toast.success(t('common.saved'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'RELEASE_VERSION_TAKEN') form.setError('version', { message: t('errors.RELEASE_VERSION_TAKEN') })
      else toastError(e)
    }
  })

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!saveChangelog.isPending}
      icon={<History />}
      title={t(entry ? 'projects.catalog.changelog.editTitle' : 'projects.catalog.changelog.createTitle')}
      description={t('projects.catalog.changelog.hint', { module: m.name })}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={saveChangelog.isPending}>
            {saveChangelog.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="cl-version">{t('projects.catalog.fields.version')}</Label>
            <Input id="cl-version" placeholder="1.4" aria-invalid={!!errors.version} {...form.register('version')} />
            {errors.version ? <p className="text-xs text-destructive">{errors.version.message}</p> : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="cl-date">{t('projects.releases.form.date')}</Label>
            <Controller control={form.control} name="date" render={({ field }) => <DateField id="cl-date" value={field.value} onChange={field.onChange} clearable={false} />} />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="cl-notes">{t('projects.releases.form.notes')}</Label>
          <Textarea id="cl-notes" rows={4} maxLength={500} aria-invalid={!!errors.notes} {...form.register('notes')} />
          {errors.notes ? <p className="text-xs text-destructive">{errors.notes.message}</p> : null}
        </div>
      </div>
    </Modal>
  )
}
